import { Injectable, inject } from "@angular/core";
import { environment } from "../../../environments/environment";
import { ConfigKey, EnvironmentName } from "../models/config";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

interface AppVersion {
  version: string;
}

@Injectable({
  providedIn: "root",
})
export class ConfigService {
  private readonly httpBackend = inject(HttpBackend);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _config: any;
  private _env: string = environment.name;
  private _initPromise: Promise<Record<string, unknown>> | null = null;
  // Create HttpClient that bypasses interceptors
  private _http: HttpClient = new HttpClient(this.httpBackend);

  load(): Promise<Record<string, unknown>> {
    if (this._initPromise) {
      return this._initPromise;
    }

    const configName = "config";
    const configUrl = `./assets/config/${configName}.json`;

    this._initPromise = firstValueFrom(this._http.get(configUrl))
      .then((config) => {
        this._config = config;
        return this._config;
      })
      .catch((error) => {
        console.error("Failed to load configuration:", error);
        throw new Error("Something went wrong while loading configuration");
      });

    return this._initPromise;
  }

  // Is app in the debug mode?
  isDebugMode() {
    return this._env === "debug";
  }

  isProduction(): boolean {
    const env = this.get(ConfigKey.ENVIRONMENT) as string;
    return env === EnvironmentName[EnvironmentName.PRD];
  }

  getEnvironment(): string {
    return this.get(ConfigKey.ENVIRONMENT) as string;
  }

  // Gets a value of specified property in the configuration file
  get(key: string) {
    if (!this._config) {
      throw new Error(
        `ConfigService: Configuration has not been loaded yet. Make sure ConfigService.load() is awaited before accessing config values.`
      );
    }
    return this._config[key];
  }

  /**
   * Fetches the application version from appversion.json file
   * @returns A Promise that resolves with the application version string
   */
  async getAppVersion(): Promise<string> {
    try {
      const result = await firstValueFrom(
        this._http.get<AppVersion>("./appversion.json")
      );
      return result.version;
    } catch (error) {
      console.error("Failed to load app version:", error);
      return "unknown";
    }
  }
}
